import { Button, Modal, Table, Tag } from "antd";
import { camelToWords, formatCurrency, formatDate, snakeToCamelCase } from "common/utils";
import {
  useFetchDebtorDisputesQuery,
  useFetchDisputeCategoriesQuery,
} from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import ManageDisputeFromAccountModal from "features/debtorAccountsTable/components/manageDisputeFromAccountModal";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useState } from "react";
import styled from "styled-components";
import { PlusOutlined } from "@ant-design/icons";

const StyledTable = styled(Table)`
  & .ant-table-cell {
    padding: 8px !important;
  }
`;

const StyledTag = styled(Tag)`
  margin-bottom: 4px;
  text-transform: uppercase;
`;

const StyledModal = styled(Modal)`
  top: 20px;
`;

const StyledButtonContainer = styled.div`
  margin-bottom: 16px;
  text-align: right;
`;

function AccountDisputesTable({ account, debtorId, open, onCancel }) {
  const [isDisputeModalVisible, setDisputeModalVisible] = useState(false);
  const [activeDispute, setActiveDispute] = useState(null);

  const {
    data: {
      [PERMISSIONS.DISPUTE__UPDATE]: isDisputeUpdateAuthorized,
      [PERMISSIONS.DISPUTE__CREATE]: isDisputeCreateAuthorized,
    } = {},
  } = useAuthorizations();

  // Fetch disputes for this specific account
  const { data: disputesData, isLoading } = useFetchDebtorDisputesQuery(
    { debtorId },
    {
      // Force refetch when refreshKey changes
      skip: !open,
    },
  );

  const { data: categories } = useFetchDisputeCategoriesQuery({
    creditorId: account?.creditorId,
  });

  const disputes = disputesData?.filter((dispute) => dispute.accountId === account?.id) || [];

  const onEditDispute = (dispute) => {
    setActiveDispute(dispute);
    setDisputeModalVisible(true);
  };

  const onOpenNewDispute = () => {
    setActiveDispute(null);
    setDisputeModalVisible(true);
  };

  const onDisputeModalCancel = () => {
    setDisputeModalVisible(false);
    setActiveDispute(null);
  };

  const onDisputeModalOk = () => {
    setDisputeModalVisible(false);
    setActiveDispute(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "resolved":
        return "green";
      case "in_review":
        return "yellow";
      case "submitted":
        return "default";
      default:
        return "blue";
    }
  };

  const columns = [
    {
      title: "Dispute ID",
      dataIndex: "id",
      key: "id",
      width: 120,
      render: (text) => text || "-",
    },
    {
      title: "Date Received",
      dataIndex: "receivedDate",
      key: "receivedDate",
      width: 120,
      render: (text) => (text ? formatDate(text) : "-"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (text) => (text ? formatCurrency(text) : "-"),
    },
    {
      title: "Categories",
      dataIndex: "categoryTagIds",
      key: "categories",
      width: 200,
      render: (categoryIds) => {
        if (!categoryIds || !Array.isArray(categoryIds) || categoryIds.length === 0) {
          return "-";
        }
        const categoryTags = categories?.filter((category) => categoryIds.includes(category.id));
        return (
          <div>
            {categoryTags?.map((tag) => (
              <StyledTag key={tag.id} color="purple">
                {tag.name}
              </StyledTag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <StyledTag color={getStatusColor(status)}>
          {status ? camelToWords(snakeToCamelCase(status)).toUpperCase() : "-"}
        </StyledTag>
      ),
    },
    {
      title: "Channel Source",
      dataIndex: "channelSource",
      key: "channelSource",
      render: (channelSource) =>
        channelSource ? camelToWords(snakeToCamelCase(channelSource)) : "-",
    },
    {
      title: "Date Resolved",
      dataIndex: "resolvedDate",
      key: "resolvedDate",
      width: 120,
      render: (text) => (text ? formatDate(text) : "-"),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
      render: (text) => text || "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      hidden: !isDisputeUpdateAuthorized,
      render: (_, record) => (
        <Button type="link" onClick={() => onEditDispute(record)}>
          Manage Dispute
        </Button>
      ),
    },
  ].filter((column) => !column.hidden);

  return (
    <>
      <StyledModal
        title={`Manage Disputes - Account ${account?.externalId}`}
        open={open}
        onCancel={onCancel}
        footer={null}
        width="90vw"
      >
        <StyledButtonContainer>
          {isDisputeCreateAuthorized && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onOpenNewDispute}
              disabled={!isDisputeCreateAuthorized}
            >
              Open Dispute
            </Button>
          )}
        </StyledButtonContainer>
        <StyledTable
          loading={isLoading}
          columns={columns}
          dataSource={disputes}
          rowKey="id"
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} disputes`,
          }}
          scroll={{ x: "max-content" }}
        />
      </StyledModal>

      {isDisputeModalVisible && (
        <ManageDisputeFromAccountModal
          account={account}
          dispute={activeDispute}
          title={`${activeDispute ? "Edit" : "Create"} Dispute`}
          open={isDisputeModalVisible}
          onOk={onDisputeModalOk}
          onCancel={onDisputeModalCancel}
          debtorId={debtorId}
        />
      )}
    </>
  );
}

export default AccountDisputesTable;
